import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Compass, Swords, Trophy, ChatCircleDots, UserCircle, Robot,
  CalendarDots, Users, GameController, Storefront
} from '@phosphor-icons/react';
import { useTenant } from '../contexts/TenantContext';
import GlobalContextSwitcher from './GlobalContextSwitcher';

const PLAYER_TABS = [
  { id: 'home', label: 'Home', icon: GameController, path: '/home' },
  { id: 'discover', label: 'Discover', icon: Compass, path: '/discover' },
  { id: 'ai', label: 'AI', icon: Robot, path: '/ai', accent: true },
  { id: 'shops', label: 'Shops', icon: Storefront, path: '/shops' },
  { id: 'profile', label: 'Profile', icon: UserCircle, path: '/profile' },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeContext, contexts } = useTenant();

  // Only show in player context or when no context is set
  const isPlayerContext = !activeContext || activeContext.tenant_type === 'personal';
  const hasOtherContexts = contexts.length > 1;

  const isActive = (path) => {
    if (path === '/home') return location.pathname === '/home';
    if (path === '/discover') return location.pathname === '/discover';
    if (path === '/shops') return location.pathname.startsWith('/shops');
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Floating Context Switcher (visible when user has multiple contexts) */}
      {/* Anchored to the bottom-left corner, ABOVE the bottom-nav bar. Previously
          this lived at `top-3 right-3` and overlapped every page's top-right
          header action (like /collection's "Add Game" button), intercepting
          taps on mobile. Bottom-left avoids ALL page-level headers (which
          typically show title/logo on the left and action buttons on the right)
          and sits within thumb reach on mobile. */}
      {hasOtherContexts && (
        <div className="fixed bottom-40 left-3 z-40" data-testid="floating-context-switcher">
          <GlobalContextSwitcher variant="mobile" />
        </div>
      )}

      {/* Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a12]/95 backdrop-blur-xl border-t border-white/[0.06] safe-area-bottom"
        data-testid="bottom-nav"
      >
        <div className="max-w-md mx-auto flex items-center justify-around px-2 py-1.5">
          {PLAYER_TABS.map(tab => {
            const Icon = tab.icon;
            const active = isActive(tab.path);
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                  tab.accent
                    ? active
                      ? 'text-[#9D4CDD]'
                      : 'text-[#9D4CDD]/60 hover:text-[#9D4CDD]'
                    : active
                      ? 'text-[#00F0FF]'
                      : 'text-[#555] hover:text-[#999]'
                }`}
                data-testid={`nav-${tab.id}`}
              >
                {tab.accent ? (
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center -mt-4 shadow-lg ${
                    active
                      ? 'bg-gradient-to-b from-[#9D4CDD] to-[#7C3AED] shadow-[#9D4CDD]/30'
                      : 'bg-gradient-to-b from-[#9D4CDD]/70 to-[#7C3AED]/70'
                  }`}>
                    <Icon size={22} weight="fill" className="text-white" />
                  </div>
                ) : (
                  <Icon size={22} weight={active ? 'fill' : 'regular'} />
                )}
                <span className={`text-[10px] font-medium ${tab.accent ? 'mt-0' : ''}`}>{tab.label}</span>
                {active && !tab.accent && (
                  <div className="w-1 h-1 rounded-full bg-[#00F0FF]" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
